import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, Typography, useTheme } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import { runInPendingChangesOperation } from 'app/services/pendingChangesOperation';
import { NoAvailableMessageContent } from 'app/shared/components/common/NoAvailableMessage';
import {
  CUSTOM_FIELDS_PARTIAL_SAVE_MESSAGE,
  CUSTOM_FIELDS_TARGET,
} from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import { buildCustomFieldsPendingChangesNode } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.utils';
import useCustomFieldsValidation from 'app/shared/components/customFields/CustomFieldsSection/hooks/useCustomFieldsValidation';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import useRemoteFieldValueSync from 'hooks/useRemoteFieldValueSync';
import useTruncatedGeneral from 'hooks/useTruncatedGeneral';
import { checkIsLoanPurposeTypeBpl } from 'utils/loanApplication';
import { getChangedFieldsByConfig, getConfigInfo } from 'utils/loanManagement';

import { showMessageAction } from 'store/global/actions';
import { savePendingBorrowerContactLinkAction } from 'store/loanApplications/actions';
import {
  selectBorrowerFieldsVisibilityBySideData,
  selectBorrowerPendingContactLinkById,
  selectBorrowersPendingChanges,
  selectCommonInfoLoanApplication,
  selectNonBorrowersPendingChanges,
  selectSubjectPropertyForResidence,
} from 'store/loanApplications/selectors';

import {
  BORROWING_ENTITY_TABS_HREF,
  EDIT_ENTITY_CONTENT_CONFIG,
  ENTITY_TYPE_HREF,
  NEW_BORROWER_REQUIRED_SECTIONS,
  NEW_ENTITY,
  NEW_ENTITY_INITIAL_DATA,
} from '../EditEntityModal.constants';

import {
  ENTITY_CREATE_DATA_ACTION_CONFIG,
  ENTITY_EDIT_DATA_ACTION_CONFIG,
  ENTITY_GET_DATA_ACTION_CONFIG,
  NORMALIZE_ENTITY_DATA_ACTION_CONFIG,
  NORMALIZE_ENTITY_GET_DATA_ACTION_CONFIG,
  REQUIRED_FIELDS_MESSAGE,
} from './Content.constants';
import {
  getWrapperStyles,
  iconWrapperStyles,
  titleStyles,
  titleWrapperStyles,
} from './Content.styles';
import {
  clearEntitiesSideDataFromEmptyData,
  getEntityRequestParams,
  getLoadDataActionParams,
  handleMissingRequiredFields,
  mergeValues,
} from './Content.utils';
import { normalizePendingChanges } from './MainContent/MainContent.utils';

const Content = ({
  setIsFormChanged,
  isSaving,
  isLoading,
  setIsLoading,
  onClose,
  additionalProps: {
    newEntities,
    deletedEntities,
    isNewEntityFlow,
    onCountPendingChanges,
    setPendingChanges,
    setInvalidTabs,
    setInvalidHeadTabs,
    data: entitiesData,
  },
  setIsSavingAllowed,
  setOnSave,
  setIsNestedEntityEditActive,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [onMouseOver, onResetTooltipState, TruncatedProvider] = useTruncatedGeneral();
  const { applicationId, section, subtab, entityId } = useParams();

  const { loanPurposeType } = useSelector(selectCommonInfoLoanApplication);
  const borrowersPendingChanges = useSelector(selectBorrowersPendingChanges);
  const nonBorrowersPendingChanges = useSelector(selectNonBorrowersPendingChanges);
  const subjectPropertyForResidence = useSelector(selectSubjectPropertyForResidence);
  const {
    positiveRentalHistory: isPositiveRentalHistoryVisible,
    caivrsNumber: isCaivrsNumberVisible,
  } = useSelector(selectBorrowerFieldsVisibilityBySideData);

  const [entitiesConfig, setEntitiesConfig] = useState({ groups: [] });
  const [entitiesApplicationData, setEntitiesApplicationData] = useState({});
  const [entitiesSideData, setEntitiesSideData] = useState({});
  const [initialEntitiesSideData, setInitialEntitiesSideData] = useState({});
  const [isError, setIsError] = useState(false);
  const [formattedPendingChanges, setFormattedPendingChanges] = useState({});
  const [isSectionSavingAllowed, setIsSectionSavingAllowed] = useState(true);

  const openedSectionsRef = useRef({});
  const formDataRef = useRef({});

  const isBorrowerFlow = subtab === ENTITY_TYPE_HREF.BORROWER;
  const isNbeFlow = subtab === ENTITY_TYPE_HREF.NON_BORROWER;
  const isNewEntity = entityId === NEW_ENTITY;
  const contentConfig = EDIT_ENTITY_CONTENT_CONFIG[subtab];
  const isDeleted = deletedEntities.includes(entityId);
  const deletedEntityMessage = isBorrowerFlow ? 'Borrower' : 'Non Borrowing Entity';
  const isPendingChangesHighlightDisabled =
    (isLoading && !isSaving) || newEntities.includes(entityId);
  const subjectPropertyAddressId = subjectPropertyForResidence?.addressId;

  const { title, icon, SectionContent } = contentConfig[section] ?? {};
  const isBplLoanPurposeType = checkIsLoanPurposeTypeBpl(loanPurposeType);

  const isCustomFieldsCapableEntity = !!entityId && entityId !== NEW_ENTITY;
  const customFieldsTarget = isBorrowerFlow
    ? CUSTOM_FIELDS_TARGET.BORROWER
    : CUSTOM_FIELDS_TARGET.NBE;
  const customFieldsValidation = useCustomFieldsValidation({
    applicationId: isCustomFieldsCapableEntity ? applicationId : null,
    target: customFieldsTarget,
    targetEntityId: isCustomFieldsCapableEntity ? entityId : null,
  });

  const isCustomFieldsSaveAllowed = customFieldsValidation.isValid;

  useEffect(() => {
    setIsSavingAllowed(
      isSectionSavingAllowed && (!isCustomFieldsCapableEntity || isCustomFieldsSaveAllowed),
    );
  }, [
    setIsSavingAllowed,
    isSectionSavingAllowed,
    isCustomFieldsCapableEntity,
    isCustomFieldsSaveAllowed,
  ]);

  useEffect(() => {
    setEntitiesSideData(clearEntitiesSideDataFromEmptyData);
  }, [section, entityId]);

  const updateEntitiesApplicationData = useCallback(data => {
    setEntitiesApplicationData(prev => ({ ...prev, ...data }));
  }, []);

  const setEntityConfig = useCallback(
    (config, isReplaceAll = false) => {
      if (_.isEmpty(entitiesApplicationData)) {
        return;
      }

      setEntitiesConfig(prev => {
        const newConfigKeys = config.groups.map(({ key }) => key);

        return {
          groups: isReplaceAll
            ? config.groups
            : // eslint-disable-next-line no-unsafe-optional-chaining
              [...prev?.groups.filter(({ key }) => !newConfigKeys.includes(key)), ...config.groups],
        };
      });
    },
    [entitiesApplicationData],
  );

  const {
    initialFormValues,
    schema,
    initialConfig,
    fieldsConfig,
    observableFieldsNames,
    fieldsNames,
  } = useMemo(
    () =>
      getConfigInfo({
        applicationData: entitiesApplicationData,
        config: entitiesConfig,
        isNestedForm: true,
      }),
    [entitiesApplicationData, entitiesConfig],
  );

  const { changedFields } = useMemo(
    () =>
      initialConfig
        ? getChangedFieldsByConfig({
            initialConfig,
            pendingChangesData: {},
          })
        : {},
    [initialConfig],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    setMultipleInForm,
    isFormChanged,
    validateField,
    validateForm,
    setInitialFormValues,
    setForm,
    setIsTouched,
    setValidationErrors,
    staticValidateField,
    setExcludedValidationPaths,
    formPendingChanges,
  } = useForm(initialFormValues, fieldsConfig, observableFieldsNames);

  const { remotePendingChanges, getCleanFormForSave, remoteInjectedFormKeys } =
    useRemoteFieldValueSync({
      config: entitiesConfig,
      isNestedForm: true,
      setForm,
      setInitialFormValues,
    });

  useEffect(() => {
    setExcludedValidationPaths(remoteInjectedFormKeys);
  }, [remoteInjectedFormKeys, setExcludedValidationPaths]);

  useEffect(() => {
    const initialPendingChanges = isBorrowerFlow
      ? borrowersPendingChanges
      : nonBorrowersPendingChanges;

    const normalizedPendingChanges = normalizePendingChanges({
      initialPendingChanges,
      formPendingChanges,
      form,
      isEditFlow: true,
      data: entitiesApplicationData,
      entitiesSideData,
      initialEntitiesSideData,
      newEntities,
      deletedEntities,
      isBorrowerFlow,
      subjectPropertyAddressId,
      isPositiveRentalHistoryVisible,
      isCaivrsNumberVisible,
    });

    const mergedWithRemote = _.merge({}, normalizedPendingChanges, remotePendingChanges);
    const customFieldsNode = buildCustomFieldsPendingChangesNode(
      customFieldsValidation.pendingChangesByDefinition,
    );
    const mergedWithCustomFields = _.merge({}, mergedWithRemote, {
      [entityId]: { customFields: customFieldsNode },
    });

    setFormattedPendingChanges(mergedWithCustomFields);
    onCountPendingChanges(mergedWithCustomFields);
    setPendingChanges(mergedWithCustomFields);
  }, [
    entityId,
    isBorrowerFlow,
    borrowersPendingChanges,
    nonBorrowersPendingChanges,
    formPendingChanges,
    setPendingChanges,
    form,
    onCountPendingChanges,
    entitiesApplicationData,
    isPendingChangesHighlightDisabled,
    entitiesSideData,
    initialEntitiesSideData,
    newEntities,
    deletedEntities,
    isDeleted,
    subjectPropertyAddressId,
    isPositiveRentalHistoryVisible,
    isCaivrsNumberVisible,
    remotePendingChanges,
    customFieldsValidation.pendingChangesByDefinition,
  ]);

  const validationErrorsRef = useRef(validationErrors);

  useEffect(() => {
    validationErrorsRef.current = validationErrors;
  }, [validationErrors]);

  const hasInvalidCustomFields = isCustomFieldsCapableEntity && !isCustomFieldsSaveAllowed;

  useEffect(() => {
    handleMissingRequiredFields({
      validationErrors,
      entityId,
      isBorrowerFlow,
      section,
      dispatch,
      setInvalidTabs,
      setInvalidHeadTabs,
      entitiesData,
      hasInvalidCustomFields,
    });
  }, [
    dispatch,
    validationErrors,
    entityId,
    section,
    isBorrowerFlow,
    setInvalidTabs,
    setInvalidHeadTabs,
    entitiesData,
    hasInvalidCustomFields,
  ]);

  useEffect(() => {
    if (openedSectionsRef.current[entityId]) {
      openedSectionsRef.current[entityId].add(section);

      return;
    }

    openedSectionsRef.current[entityId] = new Set([section]);
  }, [section, entityId]);

  useEffect(() => {
    if (isFormChanged) {
      return;
    }

    const isSideDataEqual = _.isEqual(initialEntitiesSideData, entitiesSideData);

    setIsFormChanged(!isSideDataEqual);
  }, [entitiesSideData, initialEntitiesSideData, isFormChanged, setIsFormChanged]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  useEffect(() => {
    setInitialFormValues(initialFormValues);
    setForm(form => mergeValues({ form, initialValues: initialFormValues, fieldsNames }));
  }, [initialFormValues, fieldsNames, setForm, setInitialFormValues]);

  useEffect(() => {
    if (!entitiesApplicationData[entityId]) {
      return;
    }

    formDataRef.current = form;
  }, [form, entitiesApplicationData, entityId]);

  const hasDirtyCustomFields = !_.isEmpty(customFieldsValidation.dirtyValues);

  // A pending borrower -> contact link/unlink is a change that should enable Save.
  const hasPendingCrmContactChange = useSelector(state =>
    isBorrowerFlow ? !!selectBorrowerPendingContactLinkById(state, entityId) : false,
  );

  useEffect(() => {
    setIsFormChanged(isFormChanged || hasDirtyCustomFields || hasPendingCrmContactChange);
  }, [isFormChanged, hasDirtyCustomFields, hasPendingCrmContactChange, setIsFormChanged]);

  const fetchEntityData = useCallback(() => {
    setIsError(false);
    setIsLoading(true);

    const action = ENTITY_GET_DATA_ACTION_CONFIG[subtab];
    const params = getLoadDataActionParams({
      applicationId,
      subtab,
    });

    dispatch(action(params))
      .then(res => {
        const normalizedApplicationData = NORMALIZE_ENTITY_GET_DATA_ACTION_CONFIG[subtab](res);

        updateEntitiesApplicationData(normalizedApplicationData);
      })
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [applicationId, dispatch, updateEntitiesApplicationData, setIsLoading, subtab]);

  useEffectSafeStrictMode(() => {
    if (entitiesApplicationData[entityId] || !entityId) {
      return;
    }

    if (isNewEntityFlow && entityId === NEW_ENTITY) {
      const newEntityInitialData = NEW_ENTITY_INITIAL_DATA[subtab];

      const normalizedApplicationData =
        NORMALIZE_ENTITY_GET_DATA_ACTION_CONFIG[subtab](newEntityInitialData);

      updateEntitiesApplicationData(normalizedApplicationData);

      if (isNbeFlow) {
        return;
      }
    }

    fetchEntityData();
  }, [
    entityId,
    entitiesApplicationData,
    subtab,
    fetchEntityData,
    isNewEntityFlow,
    updateEntitiesApplicationData,
    isNbeFlow,
  ]);

  const customFieldsSave = customFieldsValidation.save;

  const onSave = useCallback(async () => {
    const isNewBorrower = isNewEntityFlow && isBorrowerFlow;

    const showFillRequiredSectionsMessage = () =>
      dispatch(
        showMessageAction({
          message: REQUIRED_FIELDS_MESSAGE,
          variant: 'error',
        }),
      );

    if (!(await validateForm({ noFormChangedValidate: true }))) {
      if (isNewBorrower) {
        showFillRequiredSectionsMessage();
      }

      return;
    }

    if (isNewBorrower) {
      const isLiveTogetherForTwoYears =
        formDataRef.current[NEW_ENTITY]?.isLiveTogetherForTwoYears ?? false;

      const notFilledRequiredSections = NEW_BORROWER_REQUIRED_SECTIONS.filter(section => {
        if (isLiveTogetherForTwoYears && section === BORROWING_ENTITY_TABS_HREF.ADDRESS) {
          return false;
        }

        return !openedSectionsRef.current[NEW_ENTITY].has(section);
      });

      if (notFilledRequiredSections.length) {
        showFillRequiredSectionsMessage();

        return;
      }
    }

    setIsLoading(true);

    const isCreateNBEFlow = isNewEntityFlow && !isBorrowerFlow;

    const action = isCreateNBEFlow
      ? ENTITY_CREATE_DATA_ACTION_CONFIG[subtab]
      : ENTITY_EDIT_DATA_ACTION_CONFIG[subtab];

    const body = NORMALIZE_ENTITY_DATA_ACTION_CONFIG[subtab](
      getCleanFormForSave(formDataRef.current),
      entitiesApplicationData,
      isBplLoanPurposeType,
      entitiesSideData,
    );

    const params = getEntityRequestParams({ applicationId, body, isCreateNBEFlow });

    return runInPendingChangesOperation(() =>
      dispatch(action(params)).then(async () => {
        if (isCustomFieldsCapableEntity) {
          try {
            await customFieldsSave();
          } catch {
            dispatch(
              showMessageAction({
                message: CUSTOM_FIELDS_PARTIAL_SAVE_MESSAGE,
                variant: 'error',
              }),
            );

            return;
          }
        }

        // Persist any pending borrower -> CRM contact link now that the borrower
        // is saved. Non-blocking: a link failure must not roll back the save.
        if (isBorrowerFlow && !isNewBorrower) {
          await dispatch(
            savePendingBorrowerContactLinkAction({ borrowerId: entityId, applicationId }),
          );
        }

        setTimeout(onClose);
      }),
    )
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    dispatch,
    setIsLoading,
    validateForm,
    getCleanFormForSave,
    applicationId,
    entitiesSideData,
    onClose,
    subtab,
    isNewEntityFlow,
    entitiesApplicationData,
    isBplLoanPurposeType,
    isBorrowerFlow,
    isCustomFieldsCapableEntity,
    customFieldsSave,
    entityId,
  ]);

  useEffect(() => {
    setOnSave(() => onSave);
  }, [setOnSave, onSave]);

  return (
    <Box sx={getWrapperStyles(theme)}>
      {title && (
        <Box sx={titleWrapperStyles}>
          {icon && <Box sx={iconWrapperStyles}>{icon}</Box>}
          <Typography sx={titleStyles} variant="regular14">
            {title}
          </Typography>
        </Box>
      )}

      <ErrorHandlingLayout isError={isError} onClick={fetchEntityData}>
        <Box onMouseLeave={onResetTooltipState} onMouseOver={onMouseOver}>
          <TruncatedProvider />

          {!isDeleted ? (
            <SectionContent
              applicationData={entitiesApplicationData}
              changedFields={changedFields}
              className="form-wrapper"
              customFieldsController={customFieldsValidation}
              deletedEntities={deletedEntities}
              disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
              entitiesSideData={entitiesSideData}
              entityData={entitiesApplicationData[entityId]}
              entityId={entityId}
              form={form}
              formDataRef={formDataRef}
              handleChange={handleChange}
              initialEntitiesSideData={initialEntitiesSideData}
              initialFormValues={initialFormValues}
              isLoading={isLoading}
              isNewEntity={isNewEntity}
              isNewEntityFlow={isNewEntityFlow}
              isSaving={isSaving}
              newEntities={newEntities}
              openedSectionsRef={openedSectionsRef}
              pendingChanges={formattedPendingChanges}
              section={section}
              setEntitiesSideData={setEntitiesSideData}
              setEntityConfig={setEntityConfig}
              setForm={setForm}
              setInForm={setInForm}
              setInitialEntitiesSideData={setInitialEntitiesSideData}
              setInitialFormValues={setInitialFormValues}
              setIsLoading={setIsLoading}
              setIsNestedEntityEditActive={setIsNestedEntityEditActive}
              setIsSavingAllowed={setIsSectionSavingAllowed}
              setIsTouched={setIsTouched}
              setMultipleInForm={setMultipleInForm}
              setValidationErrors={setValidationErrors}
              staticValidateField={staticValidateField}
              updateEntitiesApplicationData={updateEntitiesApplicationData}
              validateField={validateField}
              validationErrors={validationErrors}
            />
          ) : (
            <NoAvailableMessageContent
              message={`Sorry but we could not find data for this ${deletedEntityMessage}`}
            />
          )}
        </Box>
      </ErrorHandlingLayout>
    </Box>
  );
};

Content.propTypes = {
  isSaving: PropTypes.bool.isRequired,
  setIsFormChanged: PropTypes.func.isRequired,
  setOnSave: PropTypes.func.isRequired,
  setIsNestedEntityEditActive: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  ...WithLoadingPropTypes,
};

export default memo(withLoading(Content));
